import TableSkeletonComponent from "@/app/components/ui/LoaderComponent";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import { PRIMARY_BUTTON } from "@/constants";
import { useAppoitmentStatusList } from "@/app/hooks/api_hooks/dayClosing/useAppoitmentStatusList";
import { useClinicRecevingAPI } from "@/app/hooks/api_hooks/dayClosing/useClinicRecevingAPI";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import { useUtils } from "@/app/hooks/useUtils";
import clsx from "clsx";
import { useEffect, useState } from "react";

const ReceivingClosureComponent = () => {
  const { execute: fetchDayClosingData, closureData } = useAppoitmentStatusList();
  const { execute: postClosureData } = useClinicRecevingAPI();
  const { veticDateFormat, convertToReadableFormat, POStatusTextColor } = useUtils();
  const queryParams = new URLSearchParams(location.search);
  const { NO_APPOINTMENTS } = useImageConstants();

  const queryClinics = queryParams.get("clinic_ids");
  const queryShift = queryParams.get("shift") ?? "day";

  const [notReceived, setNotReceived] = useState(true);
  const [updatedInvoices, setUpdatedInvoices] = useState(true);
  const time = queryParams.get("t") ?? "";

  useEffect(() => {
    if (queryClinics && time && queryShift) {
      fetchDayClosingData(queryClinics, queryShift, "operations", "receiving");
    }
  }, [queryClinics, queryShift, time]);

  const handleDone = () => {
    const data = {
      clinic_id: queryClinics,
      remarks: notReceived ? "I have not received anything today" : "I have updated all received invoices",
      shift: queryShift,
    };
    return postClosureData(data).then(({ status }) => {
      if (status) {
        fetchDayClosingData(queryClinics, queryShift, "operations", "receiving");
      }
    });
  };

  useEffect(() => {
    if (closureData?.length > 0) {
      setNotReceived(false);
    }
  }, [closureData]);

  const isLoading = useLoader("isClosingDataInProgress");
  const clinicsWithReceiving = closureData?.filter((clinic) => clinic?.closure_list?.length > 0) || [];

  return (
    <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
      <h2 className="text-sm font-semibold text-gray-900">Receiving Closure</h2>
      <p className="text-xs text-gray-500 mt-1">I confirm that I have checked the expiry date of each product.</p>

      <div className="flex flex-col sm:flex-row sm:items-center gap-4 my-2">

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={updatedInvoices}
            onChange={(e) => setUpdatedInvoices(e.target.checked)}
            className="w-5 h-5 text-black border-gray-300 rounded focus:ring-0"
          />
          <span className="text-gray-900 font-medium text-sm">I have updated all received invoices</span>
        </label>
        <button onClick={handleDone} className={`${PRIMARY_BUTTON} w-full sm:w-auto sm:ml-auto cursor-pointer`}>
          Done
        </button>
      </div>

      <div className="w-full">
        {isLoading ? (
          <TableSkeletonComponent columnLength={6} />
        ) : clinicsWithReceiving.length > 0 ? (
          <div className="space-y-3 overflow-y-auto max-h-[650px]">
            {clinicsWithReceiving.map((clinic) => (
              <div key={clinic.clinic_id} className="rounded-2xl border border-[#ECEEF2] bg-white p-3 shadow-sm">
                <div className="overflow-x-auto rounded-xl">
                  <table className="w-full min-w-[980px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                        <thead className="bg-[#F5F5F5] text-[#1F1F23] text-xs font-normal">
                          <tr>
                            <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">Vendor Name</th>
                            <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">PO ID</th>
                            <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">Status</th>
                            <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">Created By</th>
                          </tr>
                        </thead>
                        <tbody className="text-xs">
                          {clinic?.closure_list?.map((invoice, rowIndex) => {
                            const isFirst = rowIndex === 0;
                            const isLast = rowIndex === clinic.closure_list.length - 1;
                            const isSingle = clinic.closure_list.length === 1;

                            return (
                            <tr
                              key={invoice.po_id}
                              className={`
                                bg-white hover:bg-[#FCFCFD] transition-colors
                                ${isSingle ? "rounded-xl" : ""}
                                ${isFirst && !isSingle ? "rounded-t-xl" : ""}
                                ${isLast && !isSingle ? "rounded-b-xl" : ""}
                              `}
                            >
                              <td
                                className={`
                                  p-3 text-xs text-blue-600 whitespace-nowrap
                                  ${isSingle ? "rounded-tl-2xl rounded-bl-2xl" : ""}
                                  ${isFirst && !isSingle ? "rounded-tl-2xl" : ""}
                                  ${isLast && !isSingle ? "rounded-bl-2xl" : ""}
                                `}
                              >
                                {invoice?.vendor_details?.vendor_name || "-"}
                              </td>
                              <td className="p-3 text-xs whitespace-nowrap">{invoice?.po_number || "-"}</td>
                              <td className="p-3 text-xs">
                                <div className="w-[170px]">
                                  <div
                                    className={clsx(
                                      "flex justify-center items-center p-2 rounded-full text-black text-[10px] sm:text-xs w-auto text-center",
                                      POStatusTextColor(invoice?.po_status, true),
                                    )}
                                  >
                                    {convertToReadableFormat(invoice?.po_status)}
                                  </div>
                                </div>
                              </td>
                              <td
                                className={`
                                  p-3 text-xs whitespace-nowrap
                                  ${isSingle ? "rounded-tr-2xl rounded-br-2xl" : ""}
                                  ${isFirst && !isSingle ? "rounded-tr-2xl" : ""}
                                  ${isLast && !isSingle ? "rounded-br-2xl" : ""}
                                `}
                              >
                                <p>{invoice?.created_by || "-"}</p>
                                <p>{veticDateFormat(invoice?.created_at)}</p>
                              </td>
                            </tr>
                          );
                          })}
                        </tbody>
                      </table>
                  </div>
                  </div>
            ))}
          </div>
        ) : (
          <NoDataFoundComponent imageURL={NO_APPOINTMENTS} message="No Receving Data Found" />
        )}
      </div>
    </div>
  );
};

export default ReceivingClosureComponent;
